/**
 * UniTrustSEO — SEO surface for /uni/{id}/trust.
 *
 * Mirrors DegreeSEO.jsx (Helmet + canonical + OpenGraph + Twitter),
 * PLUS a JSON-LD `Dataset` schema so search engines can index the
 * provenance receipt as an authoritative dataset attached to the
 * university.
 *
 * `sameAs` includes the university's official domains (as https://
 * URLs) so knowledge-graph crawlers can cross-reference the record.
 */
import { Helmet } from "react-helmet-async";


const SITE = "https://whichuniversityadvisor.com";


export default function UniTrustSEO({ universityId, universityName,
                                       officialDomains = [],
                                       totalRows = 0 }) {
  if (!universityId) return null;

  const canonical = `${SITE}/uni/${universityId}/trust`;
  const ogImage   = `${SITE}/api/og/uni/${universityId}/trust.png`;
  const uniName   = universityName || "this university";
  const title     = `Provenance receipt · ${uniName} · WUA`;
  const description = (
    `Full audit trail of every data-field edit on ${uniName}'s WUA `
    + `record: who touched what, when, and where the source came `
    + `from. ${totalRows.toLocaleString()} audited rows, safe to `
    + `share with press or marketing.`
  ).slice(0, 300);

  const sameAs = (officialDomains || [])
    .map((d) => (typeof d === "string" ? d.trim() : ""))
    .filter(Boolean)
    .map((d) => (d.startsWith("http") ? d : `https://${d}`));

  const dataset = {
    "@context": "https://schema.org",
    "@type":    "Dataset",
    "name":     `${uniName} — WUA provenance receipt`,
    "url":      canonical,
    "description": description,
    "isAccessibleForFree": true,
    "keywords": [
      "university data",
      "provenance",
      "audit trail",
      "which university advisor",
      uniName,
    ],
    "creator": {
      "@type": "Organization",
      "name":  "Which University Advisor",
      "url":   SITE,
    },
    "about": {
      "@type": "CollegeOrUniversity",
      "name":  uniName,
      ...(sameAs.length ? { "sameAs": sameAs } : {}),
    },
    ...(totalRows ? { "size": `${totalRows} rows` } : {}),
    "distribution": [
      {
        "@type":     "DataDownload",
        "encodingFormat": "text/html",
        "contentUrl": canonical,
      },
    ],
  };

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph — so a shared URL previews cleanly on Slack /
          LinkedIn / press pitches. */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="WhichUniversityAdvisor" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`Provenance receipt preview card for ${uniName}`} />
      {/* Twitter card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Dataset — search-engine authoritative provenance */}
      <script type="application/ld+json">
        {JSON.stringify(dataset)}
      </script>
    </Helmet>
  );
}
